import React, { useState, useEffect, useMemo, useRef } from 'react';
import Linkify from 'react-linkify';
import ReactMarkdown from 'react-markdown';
import './AiTile.css';
import { registerAiResponseUpdater } from '../utils/externalControls';
import config from '../config';

// Helper function to process text and ensure proper line breaks
const formatResponseText = (text) => {
    if (typeof text !== 'string') return text;

    return text
        .replace(/- /g, '\n• ')  // Replace "- " with newline and bullet
        .replace(/\* /g, '\n• ') // Replace "* " with newline and bullet
        .replace(/\d+\. /g, (match) => '\n' + match) // Add newline before numbered lists
        .replace(/\\n/g, '\n')   // Replace escaped newlines with actual newlines
        .replace(/\n\n+/g, '\n\n'); // Normalize multiple consecutive newlines
};

function AiTile() {
    const [responseText, setResponseText] = useState('');
    const [backendError, setBackendError] = useState(false);
    const aiTileRef = useRef(null);

    // Backend check with delay and retry - commented out to prevent premature errors
    /*
    useEffect(() => {
        let isMounted = true;
        let intervalId = null;

        const checkBackend = async () => {
            try {
                const response = await fetch(config.TEST_PROMPT_ENDPOINT, {
                    headers: {
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                        'Pragma': 'no-cache',
                        'Expires': '0'
                    },
                    cache: 'no-store'
                });
                const data = await response.json();
                if (data.success) {
                    if (isMounted) setBackendError(false);
                    return true;
                }
            } catch (e) {
                // ignore
            }
            if (isMounted) setBackendError(true);
            return false;
        };

        // Initial delay (e.g., 8 seconds)
        const initialDelay = 8000;
        // How often to retry after initial check (e.g., every 10 seconds)
        const retryInterval = 10000;

        // Start with an initial delay, then check backend, then set up interval
        const startBackendChecks = () => {
            setTimeout(async () => {
                await checkBackend();
                intervalId = setInterval(checkBackend, retryInterval);
            }, initialDelay);
        };

        startBackendChecks();

        return () => {
            isMounted = false;
            if (intervalId) clearInterval(intervalId);
        };
    }, []);
    */

    // Array of possible AI responses with proper line breaks
    // Wrapped in useMemo to prevent recreation on each render
    const aiResponses = useMemo(() => [
        "Welcome to my portfolio! Feel free to explore my projects by scrolling down.\n\nEach project has multiple slides that you can navigate through using the arrow buttons.\n\nYou can type messages in the message bar below to better understand Samarth's skill set.",

        "Hi there! I'm Samarth, a passionate developer and engineer.\n\nCheck out my projects below to see what I've been working on.\n\nFeel free to use the message bar at the bottom to ask about my skills and experience.",

        "Interested in my areas of expertise?\n\n• AR/VR Development\n• Robotics Engineering\n• Hardware Design\n• Machine Learning\n• PCB Design\n\nType a message below to learn more about any of these skills!",

        "Thanks for visiting my portfolio website.\n\nDon't hesitate to reach out if you'd like to collaborate or learn more about any of my projects.\n\nYou can use the message bar below to ask questions about my background and expertise.",

        "My work combines hardware and software to create innovative solutions:\n\n• Embedded Systems\n• Interactive Applications\n• Computer Vision\n• Signal Processing\n• User Experience Design\n\nType in the message bar to ask about any of these areas!"
    ], []);

    // Custom component decorator for Linkify
    const componentDecorator = (href, text, key) => (
        <a href={href} key={key} target="_blank" rel="noopener noreferrer" className="ai-tile-link">
            {text}
        </a>
    );

    useEffect(() => {
        // Choose a random response from the array
        const randomIndex = Math.floor(Math.random() * aiResponses.length);
        setResponseText(aiResponses[randomIndex]);

        // Register the update function with external controls
        registerAiResponseUpdater((newText) => {
            // Process incoming text to ensure line breaks are preserved
            if (typeof newText === 'string') {
                setResponseText(formatResponseText(newText));
            } else {
                setResponseText(newText);
            }
        });
    }, [aiResponses]);

    // Add a scrolling effect when responseText changes
    useEffect(() => {
        if (responseText && aiTileRef.current) {
            // Scroll to the AI tile when new response is displayed
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });

            // Also scroll the AI tile into view
            aiTileRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    }, [responseText]);

    // Function to render text with line breaks
    const renderTextWithLineBreaks = (text) => {
        if (!text) return null;

        // Split the text by newlines and render each part
        return text.split('\n').map((line, index) => (
            <React.Fragment key={index}>
                {line}
                {index < text.split('\n').length - 1 && <br />}
            </React.Fragment>
        ));
    };

    return (
        <div className="ai-tile" ref={aiTileRef}>
            <p id="ai-response-text">
                <ReactMarkdown
                    components={{
                        a: ({ node, ...props }) => (
                            <a {...props} target="_blank" rel="noopener noreferrer" className="ai-tile-link" />
                        )
                    }}
                >
                    {responseText}
                </ReactMarkdown>
            </p>
        </div>
    );
}

export default AiTile;
